import {
  DocentSlot,
  DocentTimetableEntryDTO,
  DocentTimetableRow,
  SLOT_LABELS,
} from 'src/app/models/docent-timetable-entry.model';
import {
  DEFAULT_RANGE_IN_DAYS,
  PAST_DAYS_IN_RANGE,
} from 'src/app/utils/meeting-display';

const SLOT_ORDER: DocentSlot[] = ['Morning', 'Afternoon', 'Evening'];

export const MISSING_TEAMS_LINK_HINT =
  'Für diesen Unterrichtstag wurde noch kein Teams-Meeting angelegt. Links werden etwa 3 Wochen im Vorraus generiert.';

export function slotLabel(slot: DocentSlot): string {
  return SLOT_LABELS[slot] ?? slot;
}

function formatTime(time: string): string {
  return time.slice(0, 5); // "08:00:00" -> "08:00"
}

/** "08:00 - 14:30", oder null ohne hinterlegte Zeiten. */
export function timeRangeLabel(entry: DocentTimetableRow): string | null {
  if (!entry.startTime || !entry.endTime) return null;
  return `${formatTime(entry.startTime)} - ${formatTime(entry.endTime)}`;
}

export function isSameDay(date: string | Date, other: string | Date): boolean {
  return new Date(date).toDateString() === new Date(other).toDateString();
}

/** Ein Dozent kann ein fehlendes Meeting nicht selbst nachziehen — der Zustand wird benannt. */
export function isMissingTeamsLink(entry: DocentTimetableRow): boolean {
  return entry.meetingType === 'Teams' && !entry.meetingLink;
}

export type TimetableView = 'cards' | 'table';

/** Null ohne gemerkte Wahl und beim Server-Rendering. */
export function readStoredView(key: string): TimetableView | null {
  if (typeof localStorage === 'undefined') return null;

  const stored = localStorage.getItem(key);
  return stored === 'cards' || stored === 'table' ? stored : null;
}

export function storeView(key: string, view: TimetableView): void {
  if (typeof localStorage === 'undefined') return;

  localStorage.setItem(key, view);
}

/** Ein Unterrichtstag mit allen Abschnitten des Dozenten. */
export interface DocentTimetableDay {
  date: string;
  rows: DocentTimetableRow[];
}

/** showSlot nur, wenn ein Stundenplaneintrag mehrere Abschnitte für den Dozenten liefert. */
export function toDocentTimetableRows(
  entries: DocentTimetableEntryDTO[],
): DocentTimetableRow[] {
  const slotsPerEntry = new Map<number, number>();
  for (const entry of entries) {
    slotsPerEntry.set(
      entry.timetableEntryId,
      (slotsPerEntry.get(entry.timetableEntryId) ?? 0) + 1,
    );
  }

  return entries.map((entry) => ({
    ...entry,
    showSlot: (slotsPerEntry.get(entry.timetableEntryId) ?? 0) > 1,
  }));
}

/**
 * Gruppiert nach Unterrichtstag, aufsteigend; innerhalb des Tages nach Startzeit (ohne Zeit zuletzt),
 * dann Abschnitt, dann Kurs — wie die Reihenfolge aus dem Backend.
 */
export function groupRowsByDay(
  rows: DocentTimetableRow[],
): DocentTimetableDay[] {
  const days = new Map<string, DocentTimetableDay>();

  for (const row of rows) {
    const key = new Date(row.date).toDateString();
    const day = days.get(key);
    if (day) {
      day.rows.push(row);
    } else {
      days.set(key, { date: row.date, rows: [row] });
    }
  }

  const grouped = Array.from(days.values());
  grouped.sort((a, b) => +new Date(a.date) - +new Date(b.date));
  for (const day of grouped) {
    day.rows.sort(
      (a, b) =>
        compareStartTime(a.startTime, b.startTime) ||
        SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot) ||
        a.courseName.localeCompare(b.courseName),
    );
  }

  return grouped;
}

// "HH:mm:ss" sortiert als Text korrekt; ohne Zeit (kein Kurs-Zeitplan) ans Ende des Tages.
function compareStartTime(a?: string | null, b?: string | null): number {
  if (a && b) return a.localeCompare(b);
  if (a) return -1;
  if (b) return 1;
  return 0;
}

function defaultTimetableRange(): Date[] {
  const from = new Date();
  const to = new Date();
  from.setDate(from.getDate() - PAST_DAYS_IN_RANGE);
  to.setDate(to.getDate() + DEFAULT_RANGE_IN_DAYS);
  return [from, to];
}

function courseOptionsOf(rows: DocentTimetableRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.courseName))).sort((a, b) =>
    a.localeCompare(b),
  );
}

function applyRowFilters(
  rows: DocentTimetableRow[],
  courseName: string | null,
  topic: string,
): DocentTimetableRow[] {
  const needle = topic.trim().toLowerCase();
  return rows.filter(
    (r) =>
      (!courseName || r.courseName === courseName) &&
      (!needle || (r.topic ?? '').toLowerCase().includes(needle)),
  );
}

// Ein Eintrag gilt für Kurs + Tag, nicht den einzelnen Abschnitt → alle Zeilen des Tags markieren.
function markClassbookDayDone(
  rows: DocentTimetableRow[],
  entry: DocentTimetableRow,
): void {
  for (const row of rows) {
    if (row.courseName === entry.courseName && isSameDay(row.date, entry.date)) {
      row.hasClassbookEntry = true;
    }
  }
}

/**
 * Filterzustand des Dozenten-Stundenplans, geteilt von der internen Seite und der öffentlichen
 * Hash-Ansicht. Hält nur Zustand — Laden und Rechte bleiben bei der jeweiligen Seite.
 */
export class DocentTimetableFilterState {
  dateRange: Date[] = defaultTimetableRange();
  courseName: string | null = null;
  topic = '';

  courseOptions: string[] = [];
  rows: DocentTimetableRow[] = [];
  filtered: DocentTimetableRow[] = [];

  /** Der Zeitraum wirkt serverseitig; unvollständig ausgewählt darf nicht geladen werden. */
  get range(): { from: Date; to: Date } | null {
    const [from, to] = this.dateRange ?? [];
    return from && to ? { from, to } : null;
  }

  setRows(rows: DocentTimetableRow[]): void {
    this.rows = rows;
    this.courseOptions = courseOptionsOf(rows);
    this.apply();
  }

  apply(): void {
    this.filtered = applyRowFilters(this.rows, this.courseName, this.topic);
  }

  clear(): void {
    this.courseName = null;
    this.topic = '';
    this.apply();
  }

  markDayDone(entry: DocentTimetableRow): void {
    markClassbookDayDone(this.rows, entry);
  }
}
